import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import API_BASE_URL from '../config/api';

const FAVORITES_KEY = 'favorites_EZProcessing';

const FavoritesContext = createContext();

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) {
    throw new Error('useFavorites must be used within a FavoritesProvider');
  }
  return context;
};

// Normalize path to always use full path format: /dashboard/xxx
const normalizePath = (path) => {
  if (!path) return '';
  let normalized = path.toLowerCase().replace(/\/+$/, '');
  if (!normalized.startsWith('/dashboard/')) {
    normalized = `/dashboard/${normalized.replace(/^\/+/, '')}`;
  }
  return normalized;
};

export const FavoritesProvider = ({ children }) => {
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem(FAVORITES_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  const [loaded, setLoaded] = useState(false);
  const saveTimerRef = useRef(null);

  // Load from server on mount (merges with localStorage)
  useEffect(() => {
    const loadFromServer = async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) { setLoaded(true); return; }
        const resp = await fetch(`${API_BASE_URL}/api/Users/favorites`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (resp.ok) {
          const serverFavs = await resp.json();
          if (Array.isArray(serverFavs)) {
            // Server is the source of truth — replace local entirely
            localStorage.setItem(FAVORITES_KEY, JSON.stringify(serverFavs));
            setFavorites(serverFavs);
          }
        }
      } catch (err) {
        console.warn('Failed to load favorites from server:', err);
      }
      setLoaded(true);
    };
    loadFromServer();
  }, []);

  // Save to localStorage + debounced save to server
  const saveToServer = useCallback((favs) => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favs));
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('jwtToken');
        if (!token) return;
        await fetch(`${API_BASE_URL}/api/Users/favorites`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(favs),
        });
      } catch (err) {
        console.warn('Failed to save favorites to server:', err);
      }
    }, 1000);
  }, []);

  useEffect(() => {
    if (loaded) saveToServer(favorites);
  }, [favorites, loaded, saveToServer]);

  const toggleFavorite = (item) => {
    setFavorites((prev) => {
      const normalizedNewPath = normalizePath(item.path);
      const exists = prev.some((fav) => normalizePath(fav.path) === normalizedNewPath);
      if (exists) {
        return prev.filter((fav) => normalizePath(fav.path) !== normalizedNewPath);
      } else {
        const category = item.category || detectCategory(item.path);
        const fullPath = item.path.startsWith('/dashboard/') ? item.path : `/dashboard/${item.path.replace(/^\/+/, '')}`;
        const displayName = item.name || item.text || '';
        return [...prev, { ...item, path: fullPath, category, name: displayName, text: displayName }];
      }
    });
  };

  const isFavorite = (path) => {
    const normalizedPath = normalizePath(path);
    return favorites.some((fav) => normalizePath(fav.path) === normalizedPath);
  };

  const detectCategory = (path) => {
    const pathLower = path.toLowerCase();
    if (pathLower.includes('pdf') || pathLower.includes('ucc') || pathLower.includes('label')) return 'pdf';
    else if (pathLower.includes('excel') || pathLower.includes('xlsx') || pathLower.includes('report')) return 'excel';
    else if (pathLower.includes('link') || pathLower.includes('url')) return 'links';
    else return 'others';
  };

  const value = {
    favorites,
    setFavorites,
    toggleFavorite,
    isFavorite,
  };

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  );
};
